import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, List, X, Printer } from 'lucide-react';
import { ConceptPaperButton } from './ConceptPaperButton';
import { ImpactMeasurementButton } from './ImpactMeasurementButton';
import { PresentationLogo } from './PresentationLogo';
import { slides, slideTitles } from './slides';
import { useScreenInit } from '../useScreenInit.js';
export function Deck() {
  const screenInit = useScreenInit();
  const [currentIndex, setCurrentIndex] = useState(typeof screenInit?.slide === 'number' ? screenInit.slide : 0);
  const [direction, setDirection] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  useEffect(() => {
    if (printMode) {
      // Give the DOM time to finish all Framer Motion entrance animations
      // and load images over the network before triggering the print dialog.
      const timer = setTimeout(() => {
        window.print();
      }, 2500);
      const handleAfterPrint = () => setPrintMode(false);
      window.addEventListener('afterprint', handleAfterPrint);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('afterprint', handleAfterPrint);
      };
    }
  }, [printMode]);
  useEffect(() => {
    if (printMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        if (currentIndex < slides.length - 1) {
          setDirection(1);
          setCurrentIndex(prev => prev + 1);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        if (currentIndex > 0) {
          setDirection(-1);
          setCurrentIndex(prev => prev - 1);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        setDirection(-1);
        setCurrentIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setDirection(1);
        setCurrentIndex(slides.length - 1);
      } else if (e.key === 'Escape') {
        setTocOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, printMode]);
  const goTo = (idx: number) => {
    setDirection(idx > currentIndex ? 1 : -1);
    setCurrentIndex(idx);
  };
  const goPrev = () => currentIndex > 0 && goTo(currentIndex - 1);
  const goNext = () => currentIndex < slides.length - 1 && goTo(currentIndex + 1);
  if (printMode) {
    return <>
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center hide-on-print" data-id="element-110" data-mp="2gstgevrsurl1">
          <div className="w-12 h-12 border-4 border-[var(--brand)]/20 border-t-[var(--brand)] rounded-full animate-spin mb-4" data-id="element-111" data-mp="2lqv2zqofp919" />
          <p className="text-xl font-bold text-[var(--fg)] animate-pulse" data-id="element-112" data-mp="jz9k16elxpsf">
            Preparing PDF...
          </p>
          <p className="text-sm text-[var(--muted)] mt-2" data-id="element-113" data-mp="24gr4mlbarwn2">
            Waiting for animations and images to load
          </p>
        </div>
        <div className="print-container bg-white" data-id="element-114" data-mp="1zjsbo63ddvqg">
          {slides.map((Slide, idx) => <div key={idx} className="print-slide w-full h-full relative overflow-hidden break-after-page" data-id="element-115" data-mp="2e7vj7jx29fhd" data-mpi={idx}>
              <Slide index={idx} total={slides.length} data-id="element-116" data-mp="2n7s4rco5ly87" />
              <PresentationLogo data-id="element-117" data-mp="2n677hfagg16n" />
            </div>)}
        </div>
      </>;
  }
  const CurrentSlide = slides[currentIndex];
  return <div className="deck-root relative w-screen h-screen overflow-hidden bg-[var(--bg)]" data-id="element-118" data-mp="2x5pn5jjc10sw">
      <AnimatePresence mode="wait" custom={direction} data-id="element-119" data-mp="2s6v5wm10jkaw">
        <motion.div key={currentIndex} custom={direction} initial={{
        opacity: 0,
        x: direction > 0 ? 40 : -40
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: direction > 0 ? -40 : 40
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} className="absolute inset-0" data-id="element-120" data-mp="2s7eg1gkf94ed">
          <CurrentSlide index={currentIndex} total={slides.length} data-id="element-121" data-mp="198qaglrj6hv9" />
        </motion.div>
      </AnimatePresence>

      <PresentationLogo data-id="element-122" data-mp="1e6iak32bxaaj" />
      <ImpactMeasurementButton data-id="element-123" data-mp="gjjdhr7q60c4" />

      {/* Top progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-black/5 z-40 hide-on-print" data-id="element-124" data-mp="glckj7m92mkn">
        <motion.div className="h-full bg-[var(--brand)]" animate={{
        width: `${(currentIndex + 1) / slides.length * 100}%`
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} data-id="element-125" data-mp="lj4b9yb5s2n9" />
      </div>

      {/* Top left controls */}
      <div className="absolute top-3 left-3 z-50 flex items-center gap-2 hide-on-print" data-id="element-126" data-mp="3twbk8dilmym3">
        <button onClick={() => setTocOpen(true)} aria-label="Open table of contents" className="flex h-8 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-3 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white" data-id="element-127" data-mp="2qa8e2qt80ze">
          <List className="h-3.5 w-3.5" data-id="element-128" data-mp="26zvak7hhs3iu" />
          <span className="text-xs font-semibold" data-id="element-129" data-mp="222vdun6j8d4e">Contents</span>
        </button>

        <button onClick={() => setPrintMode(true)} aria-label="Export to PDF" className="flex h-8 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-3 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white" data-id="element-130" data-mp="224zkhcvtfq8d">
          <Printer className="h-3.5 w-3.5" data-id="element-131" data-mp="1o946nlg9x5bu" />
          <span className="text-xs font-semibold" data-id="element-132" data-mp="1jcwwthkrb9om">PDF</span>
        </button>

        <ConceptPaperButton data-id="element-133" data-mp="1ecrcynkqarh3" />
      </div>

      {/* Bottom nav cluster */}
      <div className="absolute bottom-3 right-3 z-50 flex items-center gap-2 hide-on-print" data-id="element-134" data-mp="1ef52yi0jxdhs">
        <button onClick={goPrev} disabled={currentIndex === 0} aria-label="Previous slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-135" data-mp="19g0ww08xbvvo">
          <ChevronLeft className="h-4 w-4" data-id="element-136" data-mp="usaex8vbttxj" />
        </button>
        <div className="flex items-baseline gap-1 rounded-full bg-[var(--brand)] px-3.5 py-1.5 shadow-md" data-id="element-137" data-mp="1ih3bn7jrw5t7">
          <span className="text-white font-bold text-sm tabular-nums" data-id="element-138" data-mp="1nfmwf4mmrbjz">
            {currentIndex + 1}
          </span>
          <span className="text-[var(--brand-lighter)] text-xs font-medium" data-id="element-139" data-mp="19pb1yz2kp0po">
            / {slides.length}
          </span>
        </div>
        <button onClick={goNext} disabled={currentIndex === slides.length - 1} aria-label="Next slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-140" data-mp="1doa9mrfovbkt">
          <ChevronRight className="h-4 w-4" data-id="element-141" data-mp="3lwyl8bnol2hm" />
        </button>
      </div>

      {/* Collapsible TOC */}
      <AnimatePresence data-id="element-142" data-mp="4gp20pkfknwr">
        {tocOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setTocOpen(false)} className="absolute inset-0 z-[60] bg-black/40 backdrop-blur-sm" data-id="element-143" data-mp="3vmzye8ehx0hr" />
            <motion.nav initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.35,
          ease: [0.4, 0, 0.2, 1]
        }} aria-label="Table of contents" className="absolute top-0 left-0 bottom-0 z-[70] w-[min(22rem,85vw)] bg-white shadow-2xl flex flex-col" data-id="element-144" data-mp="3cvqnbup2u3o2">
              <div className="flex items-center justify-between px-6 py-5 border-b border-black/5" data-id="element-145" data-mp="2vlwg4g8q14h5">
                <div data-id="element-146" data-mp="30g8zkqa76fsw">
                  <p className="text-[var(--brand)] text-xs font-semibold uppercase tracking-wider" data-id="element-147" data-mp="35e26jljbrdu8">
                    Campaign Strategy
                  </p>
                  <h2 className="text-[var(--fg)] font-bold text-lg" data-id="element-148" data-mp="39h77hjnrb7gr">
                    Contents
                  </h2>
                </div>
                <button onClick={() => setTocOpen(false)} aria-label="Close table of contents" className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--surface)] text-[var(--muted)]" data-id="element-149" data-mp="3jdtpm0op7kf3">
                  <X className="h-5 w-5" data-id="element-150" data-mp="1hfrr8r9tdt72" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-2" data-id="element-151" data-mp="1hhkzgb02slh6">
                {slideTitles.map((s, idx) => {
              const active = idx === currentIndex;
              return <button key={idx} onClick={() => {
                goTo(idx);
                setTocOpen(false);
              }} className={`w-full flex items-center gap-4 px-6 py-3 text-left transition-colors ${active ? 'bg-[var(--surface)]' : 'hover:bg-[var(--surface)]/60'}`} data-id="element-152" data-mp="17nly3e7tt5qa" data-mpi={idx}>
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? 'bg-[var(--brand)] text-white' : 'bg-[var(--surface)] text-[var(--muted)]'}`} data-id="element-153" data-mp="1qij2or2owoyp">
                        {idx + 1}
                      </span>
                      <span className="min-w-0" data-id="element-154" data-mp="2qddjm6j3mg7b">
                        <span className={`block text-[0.65rem] font-semibold uppercase tracking-wider ${active ? 'text-[var(--brand-light)]' : 'text-[var(--muted)]'}`} data-id="element-155" data-mp="2qe6gmfs99rc9">
                          {s.section}
                        </span>
                        <span className={`block text-sm font-semibold truncate ${active ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`} data-id="element-156" data-mp="2vbyahfcgnnj3">
                          {s.title}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </motion.nav>
          </>}
      </AnimatePresence>
    </div>;
}